// Ported from Stephanet's MissedDaysLateness.jsx (Missed Days Management).
import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlarmClock, Banknote, CheckCircle2, CircleCheckBig, CircleX, Clock, Download, Eye, Filter, MessageSquareWarning, Play, Printer, RotateCcw, Search, Users, X, XCircle,
} from 'lucide-react'
import * as hr from '../api'
import Loader from '../components/Loader'
import { useHR } from '../HRContext'
import Modal from '../components/Modal'
import { download, fmtMoney, fmtNumber, toCSV } from '../format'
import { DateInput, Select } from '../components/Controls'
import { Chip, Kpi } from './missed/shared'

// Lateness Statement: lateness per employee for a period, with each employee's late clock-ins and the HR decision on them

const EMPTY = { start: '', end: '', employee_id: '', department: '', branch: '', site: '' }
const STATUS_TONE = { 'Approved Lateness': 'green', 'Unapproved Lateness': 'red', 'Pending Review': 'yellow' }
const WARN_TONE = { 'Warning Issued': 'yellow', 'HR Query Required': 'red', 'Management Review': 'darkred' }
const PAY_TONE = { Deducted: 'red', 'No Impact': 'green', 'Pending Decision': 'yellow' }
const ACTIONS = {
  approve: ['Approve Lateness', 'Approved as permitted lateness — the company pays the late time and no deduction is made.', 'Approve', 'b-green'],
  unapprove: ['Mark Unapproved', 'The late minutes are deducted in payroll at the daily rate ÷ 480 per minute. Repeated cases raise the warning level.', 'Mark Unapproved & Deduct', 'b-red'],
  query: ['Issue HR Query', 'An HR query is issued to the employee. The case stays pending until it is approved or marked unapproved.', 'Issue Query', 'b-orange'],
  reset: ['Reset to Pending', 'The decision is undone and any deduction removed; the case returns to pending review.', 'Reset', 'b-outline'],
}

export default function MissedDaysLateness() {
  const [draft, setDraft] = useState(EMPTY)
  const [applied, setApplied] = useState(EMPTY)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [quick, setQuick] = useState('')
  const [viewing, setViewing] = useState(null)
  const [tick, setTick] = useState(0)
  const detailRef = useRef(null)

  const load = () => {
    setLoading(true)
    const q = new URLSearchParams(Object.entries(applied).filter(([, v]) => v))
    return hr.mdLateness(Object.fromEntries(q)).then(d => { setData(d); setError(''); setTick(t => t + 1) }).catch(e => setError(e.message)).finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [applied]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (viewing) setTimeout(() => detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60) }, [viewing])

  const rows = useMemo(() => {
    if (!data) return []
    const q = quick.trim().toLowerCase()
    return q ? data.rows.filter(r => [r.name, r.employee_no, r.department, r.branch, r.site].some(v => String(v || '').toLowerCase().includes(q))) : data.rows
  }, [data, quick])

  if (error && !data) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!data) return <Loader title="Loading lateness statement…" subtitle="Fetching late clock-ins" />

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }))
  const run = e => { e?.preventDefault(); setApplied({ ...draft }) }
  const clear = () => { setDraft(EMPTY); setApplied(EMPTY); setQuick('') }
  const t = data.totals
  const sum = k => rows.reduce((s, r) => s + (r[k] || 0), 0)
  const period = applied.start || applied.end ? `${applied.start || 'start'} to ${applied.end || 'date'}` : 'All dates'
  const exportCsv = () => download(`lateness-statement${applied.start ? `-${applied.start}` : ''}${applied.end ? `-to-${applied.end}` : ''}.csv`, toCSV([
    { label: 'Employee ID', get: r => r.employee_no }, { label: 'Employee Name', get: r => r.name }, { label: 'Department', get: r => r.department },
    { label: 'Branch', get: r => r.branch }, { label: 'Project Site', get: r => r.site }, { label: 'Grade', get: r => r.grade },
    { label: 'Total Cases', get: r => r.cases }, { label: 'Total Mins', get: r => r.minutes }, { label: 'Approved', get: r => r.approved },
    { label: 'Unapproved', get: r => r.unapproved }, { label: 'Pending', get: r => r.pending }, { label: 'Deduction', get: r => r.deduction || '' },
    { label: 'Warning', get: r => r.warning }, { label: 'Query', get: r => r.query },
  ], rows))

  return (
    <div className="mdr mds mdlt">
      <div className="mdr-top">
        <div>
          <h2>Lateness Statement</h2>
          <p>Employee lateness report — search, filter, and analyze lateness patterns by period</p>
        </div>
        <div className="mdr-top-actions no-print">
          {data.can.export && <button className="mdr-btn" onClick={exportCsv}><Download size={14} /> Export CSV</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={14} /> Print</button>
        </div>
      </div>
      <p className="print-only mds-print-period">Period: {period}</p>

      <form className="mdr-card mds-filters no-print" onSubmit={run}>
        <div className="mds-card-title"><Filter size={15} /> Report Filters</div>
        <div className="mds-grid">
          <label>From Date<DateInput value={draft.start} max={draft.end || undefined} onChange={e => set('start', e.target.value)} /></label>
          <label>To Date<DateInput value={draft.end} min={draft.start || undefined} onChange={e => set('end', e.target.value)} /></label>
          <label>Employee Name / ID
            <Select value={draft.employee_id} onChange={e => set('employee_id', e.target.value)}>
              <option value="">All employees</option>
              {data.employees.map(x => <option key={x.id} value={x.id}>{x.employee_no} — {x.name}{x.status && !['active', 'probation'].includes(x.status) ? ` (${x.status})` : ''}</option>)}
            </Select>
          </label>
          <label>Department
            <Select value={draft.department} onChange={e => set('department', e.target.value)}>
              <option value="">All</option>{data.departments.map(d => <option key={d}>{d}</option>)}
            </Select>
          </label>
          <label>Branch
            <Select value={draft.branch} onChange={e => set('branch', e.target.value)}>
              <option value="">All</option>{data.branches.map(d => <option key={d}>{d}</option>)}
            </Select>
          </label>
          <label>Project Site<input placeholder="Filter site..." value={draft.site} onChange={e => set('site', e.target.value)} /></label>
        </div>
        <div className="mds-filter-actions">
          <button type="submit" className="mds-run" disabled={loading}><Play size={14} /> {loading ? 'Running…' : 'Run'}</button>
          <button type="button" className="mdr-btn" onClick={clear}><RotateCcw size={13} /> Clear Filter</button>
          {error && <span className="mds-error">{error}</span>}
        </div>
      </form>

      <div className="md-kpis mds-kpis mdlt-kpis" key={`k${tick}`}>
        {[['Employees Affected', t.employees, Users, 'blue', 'With late clock-ins'], ['Total Lateness Cases', t.cases, AlarmClock, 'orange', period],
          ['Total Minutes Late', t.minutes, Clock, 'amber', `${(t.minutes / 60).toFixed(1)} hours`], ['Approved Lateness', t.approved, CircleCheckBig, 'green', 'No deduction'],
          ['Unapproved Lateness', t.unapproved, CircleX, 'red', `${fmtNumber(t.pending)} pending review`], ['Total Deduction', fmtMoney(t.deduction), Banknote, 'rose', 'Payroll impact']]
          .map(([label, value, Ico, tone, note], i) => <Kpi key={label} {...{ label, value, Ico, tone, note, i }} />)}
      </div>

      <div className="mdr-card mdr-table-card">
        <div className="mds-table-head">
          <b>Lateness Summary by Employee — {rows.length} employee(s)</b>
          <div className="mdr-search mds-quick no-print"><Search size={14} /><input placeholder="Quick search..." value={quick} onChange={e => setQuick(e.target.value)} /></div>
        </div>
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>
              <th>S/N</th><th>Employee ID</th><th>Employee Name</th><th>Department</th><th>Branch</th><th>Project Site</th><th>Grade</th>
              <th className="c">Total Cases</th><th className="c">Total Mins</th><th className="c">Approved</th><th className="c">Unapproved</th><th className="c">Pending</th>
              <th className="r">Deduction</th><th>Warning</th><th>Query</th><th className="no-print">Action</th>
            </tr></thead>
            <tbody key={tick}>
              {rows.map((r, i) => (
                <tr key={r.employee_id} className={`mdr-row ${viewing === r.employee_id ? 'picked' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 22}ms` }}>
                  <td>{i + 1}</td><td className="mdr-emp">{r.employee_no}</td><td className="mdr-name">{r.name}</td><td>{r.department}</td>
                  <td className="nowrap">{r.branch || '—'}</td><td className="nowrap">{r.site || '—'}</td><td className="nowrap">{r.grade || '—'}</td>
                  <td className="c b">{r.cases}</td><td className="c am">{fmtNumber(r.minutes)}</td><td className="c g">{r.approved}</td>
                  <td className="c rd">{r.unapproved}</td><td className="c in">{r.pending}</td>
                  <td className="r mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</td>
                  <td><Chip tone={WARN_TONE[r.warning]}>{r.warning}</Chip></td><td><Chip tone="purple">{r.query}</Chip></td>
                  <td className="no-print"><button className="mds-view" onClick={() => setViewing(r.employee_id)}><Eye size={13} /> View</button></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={16} className="mdr-empty">No late clock-ins for the selected filters.</td></tr>}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr>
                <td colSpan={7}>TOTALS</td>
                {['cases', 'minutes', 'approved', 'unapproved', 'pending'].map(k => <td key={k} className="c">{fmtNumber(sum(k))}</td>)}
                <td className="r">{fmtMoney(sum('deduction'))}</td><td colSpan={3} />
              </tr></tfoot>
            )}
          </table>
        </div>
        <div className="mdr-foot">
          <span>Period: <b>{period}</b> · Showing {rows.length} of {data.rows.length} employee(s)</span>
          <span>Total Deduction: <strong>{fmtMoney(sum('deduction'))}</strong></span>
        </div>
      </div>

      {viewing && <div ref={detailRef}><Detail id={viewing} period={applied} can={data.can.review} onClose={() => setViewing(null)} onChanged={load} /></div>}
    </div>
  )
}

function Detail({ id, period, can, onClose, onChanged }) {
  const { notify } = useHR()
  const [d, setD] = useState(null)
  const [form, setForm] = useState(null) // {action, row}
  const [busy, setBusy] = useState(false)
  const load = () => {
    const q = new URLSearchParams(Object.entries({ start: period.start, end: period.end }).filter(([, v]) => v))
    return hr.mdLatenessEmployee(id, Object.fromEntries(q)).then(setD).catch(e => notify(e.message, 'bad'))
  }
  useEffect(() => { setD(null); load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  const run = (action, rows, extra) => {
    setBusy(true)
    hr.mdLatenessAction({ ids: rows.map(r => r.id), action, ...extra })
      .then(r => { notify(`${r.done} lateness case(s) updated`); setForm(null); return Promise.all([load(), onChanged()]) })
      .catch(e => notify(e.message, 'bad')).finally(() => setBusy(false))
  }
  if (!d) return <Loader title="Loading lateness detail…" subtitle="Fetching the employee's late clock-ins" />
  const e = d.employee
  const pending = d.rows.filter(r => r.status === 'Pending Review')
  const csv = () => download(`${e.employee_no}-lateness.csv`, toCSV([
    { label: 'Date', get: r => r.date }, { label: 'Day', get: r => r.weekday }, { label: 'Expected In', get: r => r.expected }, { label: 'Actual In', get: r => r.actual },
    { label: 'Mins Late', get: r => r.minutes }, { label: 'Hrs Late', get: r => r.hours }, { label: 'Reason', get: r => r.reason }, { label: 'Explanation', get: r => r.explanation },
    { label: 'Approval Status', get: r => r.status }, { label: 'HR Decision', get: r => r.hr_decision }, { label: 'Payroll Impact', get: r => r.payroll },
    { label: 'Deduction', get: r => r.deduction || '' }, { label: 'Warning', get: r => r.warning }, { label: 'Action By', get: r => r.action_by },
    { label: 'Action Date', get: r => r.action_date }, { label: 'Remark', get: r => r.remark },
  ], d.rows))

  return (
    <div className="mdr-card mdr-table-card mdlt-detail">
      <div className="mdlt-detail-head">
        <div><h3>{e.name} — Lateness Detail</h3><p>{[e.employee_no, e.department].filter(Boolean).join(' | ')}</p></div>
        <div className="mdr-top-actions no-print">
          {can && pending.length > 0 && <button className="mdr-bulk-btn b-green" disabled={busy} onClick={() => setForm({ action: 'approve', rows: pending })}><CheckCircle2 size={14} /> Approve All Pending ({pending.length})</button>}
          <button className="mdr-btn" onClick={csv}><Download size={14} /> Export</button>
          <button className="mdr-btn" onClick={onClose}><X size={14} /> Close</button>
        </div>
      </div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table">
          <thead><tr>
            {can && <th className="no-print">Actions</th>}
            <th>Date</th><th>Day</th><th>Expected In</th><th>Actual In</th><th className="c">Mins Late</th><th className="c">Hrs Late</th><th>Reason</th><th>Explanation</th>
            <th>Approval Status</th><th>HR Decision</th><th>Payroll Impact</th><th className="r">Deduction</th><th>Warning</th><th>Action By</th><th>Action Date</th><th>Remark</th>
          </tr></thead>
          <tbody>
            {d.rows.map((r, i) => (
              <tr key={r.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 22}ms` }}>
                {can && <td className="no-print">
                  <div className="mdr-actions">
                    {r.status !== 'Approved Lateness' && <button className="mdr-act a-green" title="Approve lateness" disabled={busy} onClick={() => setForm({ action: 'approve', rows: [r] })}><CircleCheckBig size={12} /></button>}
                    {r.status !== 'Unapproved Lateness' && <button className="mdr-act a-red" title={`Mark unapproved — deduct ${fmtMoney(r.proposed)}`} disabled={busy} onClick={() => setForm({ action: 'unapprove', rows: [r] })}><XCircle size={12} /></button>}
                    {!r.query && r.status === 'Pending Review' && <button className="mdr-act a-purple" title="Issue HR query" disabled={busy} onClick={() => setForm({ action: 'query', rows: [r] })}><MessageSquareWarning size={12} /></button>}
                    {(r.status !== 'Pending Review' || r.query) && <button className="mdr-act a-gray" title="Reset to pending" disabled={busy} onClick={() => setForm({ action: 'reset', rows: [r] })}><RotateCcw size={12} /></button>}
                  </div>
                </td>}
                <td className="nowrap">{r.date}</td><td>{r.weekday}</td><td>{r.expected}</td><td><b className="neg">{r.actual}</b></td>
                <td className="c"><b>{r.minutes}</b></td><td className="c">{r.hours.toFixed(2)}</td>
                <td><span className="mds-wrap">{r.reason || '—'}</span></td><td><span className="mds-wrap">{r.explanation || '—'}</span></td>
                <td><Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip></td>
                <td className="nowrap">{r.hr_decision}{r.query && <Chip tone="purple">{r.query}</Chip>}</td>
                <td><Chip tone={PAY_TONE[r.payroll]}>{r.payroll}</Chip></td>
                <td className="r mdr-ded">{r.deduction ? <span className="neg">{fmtMoney(r.deduction)}</span> : '—'}</td>
                <td><Chip tone={WARN_TONE[r.warning]}>{r.warning}</Chip></td>
                <td className="nowrap">{r.action_by || '—'}</td><td className="nowrap">{r.action_date || '—'}</td>
                <td><span className="mds-wrap">{r.remark || '—'}</span></td>
              </tr>
            ))}
            {!d.rows.length && <tr><td colSpan={17} className="mdr-empty">No late clock-ins in this period.</td></tr>}
          </tbody>
          {d.rows.length > 0 && <tfoot><tr>
            <td colSpan={can ? 5 : 4}>TOTALS — {d.rows.length} case(s)</td><td className="c">{fmtNumber(d.rows.reduce((s, r) => s + r.minutes, 0))}</td>
            <td className="c">{(d.rows.reduce((s, r) => s + r.minutes, 0) / 60).toFixed(2)}</td><td colSpan={5} />
            <td className="r">{fmtMoney(d.rows.reduce((s, r) => s + r.deduction, 0))}</td><td colSpan={4} />
          </tr></tfoot>}
        </table>
      </div>
      {form && <ActionModal {...form} name={e.name} busy={busy} onClose={() => setForm(null)} onSubmit={extra => run(form.action, form.rows, extra)} />}
    </div>
  )
}

function ActionModal({ action, rows, name, busy, onClose, onSubmit }) {
  const [explanation, setExplanation] = useState(rows.length === 1 ? rows[0].explanation || '' : '')
  const [remark, setRemark] = useState('')
  const [title, intro, label, tone] = ACTIONS[action]
  const deduct = rows.reduce((s, r) => s + r.proposed, 0)
  return (
    <Modal className="mdl-modal" title={<><AlarmClock size={18} /> {title} — {name}</>} onClose={onClose}>
      <form className="mdl-modal-body" onSubmit={e => { e.preventDefault(); onSubmit({ explanation, remark }) }}>
        <p className="mdl-modal-lead">{intro}</p>
        <div className="mdlt-cases">
          {rows.map(r => <div key={r.id}><b>{r.date}</b><span>{r.weekday} · {r.actual} (expected {r.expected})</span><em>{r.minutes} mins late</em></div>)}
        </div>
        {action === 'unapprove' && <div className="mdl-warn">Deduction: <b>{fmtMoney(deduct)}</b> ({rows.reduce((s, r) => s + r.minutes, 0)} mins × daily rate ÷ 480)</div>}
        {rows.length === 1 && <label className="mdh-field">Employee Explanation
          <textarea rows={2} value={explanation} onChange={e => setExplanation(e.target.value)} placeholder="What the employee said about the lateness…" /></label>}
        <label className="mdh-field">Remark{action === 'query' ? ' *' : ''}
          <textarea rows={2} value={remark} required={action === 'query'} onChange={e => setRemark(e.target.value)} placeholder="Note for the record…" /></label>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" onClick={onClose}>Cancel</button>
          <button type="submit" className={`mdr-bulk-btn ${tone}`} disabled={busy || (action === 'query' && !remark.trim())}>{busy ? 'Saving…' : label}</button>
        </div>
      </form>
    </Modal>
  )
}
